import { FitnessClass } from "@/types/fitness-class";

export const getClassImage = (fitnessClass: FitnessClass): string => {
  return (
    fitnessClass.media?.image ||
    "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?ixlib=rb-4.0.3"
  );
};

export const getClassPrice = (fitnessClass: FitnessClass): number => {
  if (!fitnessClass.pricing) return 0;

  switch (fitnessClass.pricing.mode) {
    case "payPerSession":
      return fitnessClass.pricing.sessionPrice;
    case "recurringClass":
      return fitnessClass.pricing.packagePrice; // Return package price for recurring classes
    case "classPack":
      return (
        fitnessClass.pricing.totalPrice / fitnessClass.pricing.classesIncluded
      );
    case "subscription":
      return (
        fitnessClass.pricing.totalPrice / fitnessClass.pricing.durationDays
      );
    case "course":
      return (
        fitnessClass.pricing.totalPrice / fitnessClass.pricing.totalSessions
      );
    case "consultation":
      return fitnessClass.pricing.price;
    default:
      return 0;
  }
};

export const getBaseAmount = (fitnessClass: FitnessClass): number => {
  if (!fitnessClass?.pricing) return 2000; // Default base amount

  switch (fitnessClass.pricing.mode) {
    case "payPerSession":
      return fitnessClass.pricing.sessionPrice || 2000;
    case "recurringClass":
      return fitnessClass.pricing.packagePrice || 2000;
    case "classPack":
      return fitnessClass.pricing.totalPrice || 2000;
    case "subscription":
      return fitnessClass.pricing.totalPrice || 2000;
    case "course":
      return fitnessClass.pricing.totalPrice || 2000;
    case "consultation":
      return fitnessClass.pricing.price || 2000;
    default:
      return 2000;
  }
};

export const getClassDuration = (fitnessClass: FitnessClass): string => {
  return `${fitnessClass.schedule.durationMin || 60} minutes`;
};

export const getClassDateTime = (
  fitnessClass: FitnessClass
): { date: string; time: string } => {
  if (!fitnessClass.schedule) return { date: "TBD", time: "TBD" };

  try {
    switch (fitnessClass.schedule.kind) {
      case "single":
        if (!fitnessClass.schedule.date) return { date: "TBD", time: "TBD" };
        const singleDate = new Date(fitnessClass.schedule.date);
        if (isNaN(singleDate.getTime())) return { date: "TBD", time: "TBD" };
        return {
          date: singleDate.toLocaleDateString(),
          time: singleDate.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          }),
        };
      case "recurring":
        if (!fitnessClass.schedule.startDate) return { date: "TBD", time: fitnessClass.schedule.startTime || "TBD" };
        const recurringDate = new Date(fitnessClass.schedule.startDate);
        if (isNaN(recurringDate.getTime())) return { date: "TBD", time: fitnessClass.schedule.startTime || "TBD" };
        return {
          date: recurringDate.toLocaleDateString(),
          time: fitnessClass.schedule.startTime || "TBD",
        };
      case "course":
        if (
          fitnessClass.schedule.sessionDates &&
          fitnessClass.schedule.sessionDates.length > 0
        ) {
          const firstSessionDate = new Date(fitnessClass.schedule.sessionDates[0]);
          if (isNaN(firstSessionDate.getTime())) return { date: "TBD", time: "TBD" };
          return {
            date: firstSessionDate.toLocaleDateString(),
            time: firstSessionDate.toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
            }),
          };
        }
        return { date: "TBD", time: "TBD" };
      default:
        return { date: "TBD", time: "TBD" };
    }
  } catch (error) {
    console.error("Error in getClassDateTime:", error);
    return { date: "TBD", time: "TBD" };
  }
};

/**
 * Check if a class is expired (all sessions are in the past)
 */
export const isClassExpired = (fitnessClass: FitnessClass): boolean => {
  if (!fitnessClass.schedule) return false;

  try {
    const now = new Date();

    switch (fitnessClass.schedule.kind) {
      case "single":
        // Single class is expired if the date is in the past
        if (!fitnessClass.schedule.date) return false;
        const classDate = new Date(fitnessClass.schedule.date);
        if (isNaN(classDate.getTime())) return false; // Invalid date = not expired
        return classDate < now;

      case "recurring":
        // Recurring class is expired only if it has an endDate and it's in the past
        if (!fitnessClass.schedule.endDate) return false; // No end date = ongoing
        const endDate = new Date(fitnessClass.schedule.endDate);
        if (isNaN(endDate.getTime())) return false; // Invalid date = not expired
        return endDate < now;

      case "course":
        // Course is expired if all session dates are in the past
        if (
          !fitnessClass.schedule.sessionDates ||
          fitnessClass.schedule.sessionDates.length === 0
        ) {
          return false;
        }
        // Check if at least one session is in the future
        const hasFutureSession = fitnessClass.schedule.sessionDates.some(
          (sessionDate: string) => {
            try {
              const sessionDateObj = new Date(sessionDate);
              if (isNaN(sessionDateObj.getTime())) return false;
              return sessionDateObj > now;
            } catch {
              return false;
            }
          }
        );
        return !hasFutureSession; // Expired if no future sessions

      default:
        return false;
    }
  } catch (error) {
    console.error("Error in isClassExpired:", error);
    return false; // On error, assume not expired
  }
};
